'use client';
import React, { useState, useEffect } from 'react';
import { X, Save, FolderOpen, Trash2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import type { CanvasNode, CanvasEdge, SavedArchitecture } from '@/lib/archsim-data';

interface SaveLoadModalProps {
  nodes: CanvasNode[];
  edges: CanvasEdge[];
  onLoad: (arch: SavedArchitecture) => void;
  onClose: () => void;
}

const STORAGE_KEY = 'archsim-saved-architectures';
const MAX_SAVES = 20;

function loadSaved(): SavedArchitecture[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveToDB(archs: SavedArchitecture[]) {
  // Backend integration point: POST /api/architectures
  localStorage.setItem(STORAGE_KEY, JSON.stringify(archs));
}

export default function SaveLoadModal({ nodes, edges, onLoad, onClose }: SaveLoadModalProps) {
  const [tab, setTab] = useState<'save' | 'load'>('save');
  const [saved, setSaved] = useState<SavedArchitecture[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSaved(loadSaved());
  }, []);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Please enter an architecture name'); return; }
    if (nodes.length === 0) { toast.error('Canvas is empty — add some components first'); return; }
    setSaving(true);

    await new Promise(r => setTimeout(r, 400));

    const arch: SavedArchitecture = {
      id: `arch-${Date.now()}`,
      name: name.trim(),
      description: description.trim(),
      nodes,
      edges,
      savedAt: new Date().toISOString(),
      tags: tags.split(',').map(t => t.trim()).filter(Boolean),
    };

    const updated = [arch, ...saved].slice(0, MAX_SAVES);
    saveToDB(updated);
    setSaved(updated);
    setSaving(false);
    toast.success(`"${arch.name}" saved successfully`);
    setName('');
    setDescription('');
    setTags('');
    setTab('load');
  };

  const handleDelete = (id: string) => {
    const updated = saved.filter(a => a.id !== id);
    saveToDB(updated);
    setSaved(updated);
    toast.success('Architecture deleted');
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-card border border-border rounded-xl w-full max-w-lg overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground">Save / Load Architectures</h2>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-all">
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          {(['save', 'load'] as const).map(t => (
            <button
              key={`sl-tab-${t}`}
              onClick={() => setTab(t)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-all ${
                tab === t ? 'tab-active' : 'tab-inactive'
              }`}
            >
              {t === 'save' ? <Save size={13} /> : <FolderOpen size={13} />}
              {t === 'save' ? 'Save Current' : `Load Saved (${saved.length}/${MAX_SAVES})`}
            </button>
          ))}
        </div>

        <div className="p-6">
          {tab === 'save' && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Architecture Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  placeholder="e.g. E-commerce with Redis Cache"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Description</label>
                <textarea
                  placeholder="What does this architecture demonstrate?"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={3}
                  className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-ring resize-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Tags</label>
                <p className="text-xs text-muted-foreground mb-1.5">Comma-separated: caching, microservices, event-driven</p>
                <input
                  type="text"
                  placeholder="caching, postgres, kafka"
                  value={tags}
                  onChange={e => setTags(e.target.value)}
                  className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground p-3 bg-secondary rounded-md">
                <span className="text-lg">💾</span>
                <span>Saves to browser localStorage — {nodes.length} nodes, {edges.length} edges</span>
              </div>
              <button
                onClick={handleSave}
                disabled={saving || nodes.length === 0}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    Saving...
                  </span>
                ) : (
                  <><Save size={14} /> Save Architecture</>
                )}
              </button>
            </div>
          )}

          {tab === 'load' && (
            <div className="space-y-2 max-h-80 overflow-y-auto scrollbar-thin">
              {saved.length === 0 ? (
                <div className="text-center py-8">
                  <FolderOpen size={28} className="text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No saved architectures yet</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">Build something on the canvas and save it</p>
                </div>
              ) : (
                saved.map(arch => (
                  <div key={arch.id} className="flex items-start gap-3 p-3 rounded-lg border border-border bg-secondary hover:border-primary/30 transition-all group">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{arch.name}</p>
                      {arch.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">{arch.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1.5">
                        <Clock size={10} className="text-muted-foreground/60" />
                        <span className="text-xs text-muted-foreground/60 font-mono">{formatDate(arch.savedAt)}</span>
                        <span className="text-xs text-muted-foreground font-mono-nums">{arch.nodes.length}N · {arch.edges.length}E</span>
                      </div>
                      {arch.tags.length > 0 && (
                        <div className="flex gap-1 mt-1.5 flex-wrap">
                          {arch.tags.slice(0, 4).map(tag => (
                            <span key={`tag-${arch.id}-${tag}`} className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => onLoad(arch)}
                        className="px-2.5 py-1 rounded-md bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-all"
                      >
                        Load
                      </button>
                      <button
                        onClick={() => handleDelete(arch.id)}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-danger hover:bg-danger/10 transition-all"
                        title="Delete this architecture"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}